import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Package, Pencil, Trash2, Upload, X, Star, Search, Image, Layers, Sparkles, Loader2, SpellCheck, ArrowUpRight, GripVertical, Tag, FileUp } from 'lucide-react';
import { CsvImportDialog } from '@/components/CsvImportDialog';
import VariantManager from '@/components/VariantManager';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ShopProducts = () => {
  const { shopId } = useParams();
  const { api, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageManagerOpen, setImageManagerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [selectedProductForImages, setSelectedProductForImages] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [variantManagerOpen, setVariantManagerOpen] = useState(false);
  const [selectedProductForVariants, setSelectedProductForVariants] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [correctingAI, setCorrectingAI] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    priceCents: '',
    description: '',
    isFeatured: false,
    isAvailable: true,
    upsellProductIds: [],
    promoPriceCents: '',
    promoStartDate: '',
    promoEndDate: ''
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        api.get(`/shops/${shopId}/products`),
        api.get(`/shops/${shopId}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [api, shopId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.categoryId) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: formData.name,
        categoryId: formData.categoryId,
        priceCents: formData.priceCents ? parseInt(formData.priceCents) : null,
        description: formData.description || null,
        isFeatured: formData.isFeatured,
        isAvailable: formData.isAvailable,
        upsellProductIds: formData.upsellProductIds || [],
        promoPriceCents: formData.promoPriceCents ? parseInt(formData.promoPriceCents) : null,
        promoStartDate: formData.promoStartDate || null,
        promoEndDate: formData.promoEndDate || null
      };
      if (editingProduct) {
        const response = await api.put(`/shops/${shopId}/products/${editingProduct.id}`, data);
        setProducts(products.map(p => p.id === editingProduct.id ? response.data : p));
        toast.success('Produit modifié');
      } else {
        const response = await api.post(`/shops/${shopId}/products`, data);
        setProducts([...products, response.data]);
        toast.success('Produit créé');
      }
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!productToDelete) return;
    try {
      await api.delete(`/shops/${shopId}/products/${productToDelete.id}`);
      setProducts(products.filter(p => p.id !== productToDelete.id));
      toast.success('Produit supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleMultipleImageUpload = async (productId, files) => {
    setUploadingImage(true);
    let uploadedCount = 0;
    const newImages = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const response = await api.post(`/shops/${shopId}/products/${productId}/images`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newImages.push(response.data);
        uploadedCount++;
      } catch (error) {
        toast.error(`Erreur pour ${file.name}`);
      }
    }
    if (uploadedCount > 0) {
      setProducts(prev => prev.map(p => {
        if (p.id === productId) {
          const imgs = p.images || [];
          return { ...p, images: [...imgs, ...newImages] };
        }
        return p;
      }));
      if (selectedProductForImages?.id === productId) {
        setSelectedProductForImages(prev => ({
          ...prev,
          images: [...(prev.images || []), ...newImages]
        }));
      }
      toast.success(`${uploadedCount} image${uploadedCount > 1 ? 's' : ''} ajoutée${uploadedCount > 1 ? 's' : ''}`);
    }
    setUploadingImage(false);
  };

  const handleDeleteImage = async (productId, imageId) => {
    try {
      await api.delete(`/shops/${shopId}/products/${productId}/images/${imageId}`);
      setProducts(products.map(p => {
        if (p.id === productId) {
          return { ...p, images: (p.images || []).filter(i => i.id !== imageId) };
        }
        return p;
      }));
      if (selectedProductForImages?.id === productId) {
        setSelectedProductForImages(prev => ({
          ...prev,
          images: (prev.images || []).filter(i => i.id !== imageId)
        }));
      }
      toast.success('Image supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleReorderImages = async (productId, newImages) => {
    // Update local state immediately
    setSelectedProductForImages(prev => ({ ...prev, images: newImages }));
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, images: newImages } : p));
    
    try {
      await api.post(`/shops/${shopId}/products/${productId}/images/reorder`, newImages.map(i => i.id));
    } catch (error) {
      toast.error('Erreur lors du réordonnancement');
    }
  };

  const toggleFeatured = async (product) => {
    if (!product.isFeatured && user?.plan === 'STARTER') {
      toast.error('Disponible à partir du plan PRO');
      return;
    }
    try {
      const response = await api.put(`/shops/${shopId}/products/${product.id}`, {
        isFeatured: !product.isFeatured
      });
      setProducts(products.map(p => p.id === product.id ? response.data : p));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', categoryId: '', priceCents: '', description: '', isFeatured: false, isAvailable: true, upsellProductIds: [], promoPriceCents: '', promoStartDate: '', promoEndDate: '' });
    setEditingProduct(null);
  };

  const handleGenerateDescription = async () => {
    if (!formData.name.trim()) {
      toast.error('Entrez un nom de produit pour générer une description');
      return;
    }
    setGeneratingAI(true);
    try {
      const res = await api.post('/ai/generate', { text: formData.name, type: 'description' });
      setFormData(prev => ({ ...prev, description: res.data.result }));
      toast.success('Description générée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleCorrectDescription = async () => {
    if (!formData.description.trim()) {
      toast.error('Entrez une description à corriger');
      return;
    }
    setCorrectingAI(true);
    try {
      const res = await api.post('/ai/generate', { text: formData.description, type: 'correct' });
      setFormData(prev => ({ ...prev, description: res.data.result }));
      toast.success('Description corrigée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la correction');
    } finally {
      setCorrectingAI(false);
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      categoryId: product.categoryId,
      priceCents: product.priceCents?.toString() || '',
      description: product.description || '',
      isFeatured: product.isFeatured,
      isAvailable: product.isAvailable,
      upsellProductIds: product.upsellProductIds || [],
      promoPriceCents: product.promoPriceCents?.toString() || '',
      promoStartDate: product.promoStartDate || '',
      promoEndDate: product.promoEndDate || ''
    });
    setDialogOpen(true);
  };

  const formatPrice = (cents) => {
    if (!cents) return '-';
    return (cents / 100).toFixed(2) + ' €';
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : 'Sans catégorie';
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.categoryId === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = filteredProducts.findIndex(p => p.id === active.id);
    const newIndex = filteredProducts.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(filteredProducts, oldIndex, newIndex);
    
    // Update local state
    const reorderedIds = new Set(reordered.map(p => p.id));
    const otherProducts = products.filter(p => !reorderedIds.has(p.id));
    setProducts([...reordered, ...otherProducts]);

    try {
      await api.post(`/shops/${shopId}/products/reorder`, reordered.map(p => p.id));
      toast.success('Ordre mis à jour');
    } catch (error) {
      toast.error('Erreur lors du réordonnancement');
      fetchData();
    }
  };

  const planLimit = user?.plan === 'STARTER' ? 30 : Infinity;
  const canCreate = products.length < planLimit;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shop-products">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Produits</h1>
          <p className="text-muted-foreground mt-1">
            {products.length} produit{products.length > 1 ? 's' : ''}
            {user?.plan === 'STARTER' && ` / ${planLimit} max`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setCsvImportOpen(true)} data-testid="csv-import-open-btn">
            <FileUp className="w-4 h-4 mr-2" />
            Importer CSV
          </Button>
          <Button className="rounded-full" onClick={() => { resetForm(); setDialogOpen(true); }} disabled={!canCreate || categories.length === 0} data-testid="create-product-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau produit
          </Button>
        </div>
      </div>

      {categories.length === 0 && (
        <Card className="border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/20">
          <CardContent className="py-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              Créez d'abord au moins une catégorie avant d'ajouter des produits.
            </p>
          </CardContent>
        </Card>
      )}

      {products.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              {categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucun produit</h3>
            <p className="text-muted-foreground mb-6">Ajoutez votre premier produit</p>
          </CardContent>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredProducts.map(p => p.id)} strategy={rectSortingStrategy}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredProducts.map((product) => (
                <SortableProductCard 
                  key={product.id}
                  product={product}
                  getCategoryName={getCategoryName}
                  formatPrice={formatPrice}
                  onEdit={() => openEditDialog(product)}
                  onDelete={() => { setProductToDelete(product); setDeleteDialogOpen(true); }}
                  onToggleFeatured={() => toggleFeatured(product)}
                  onManageImages={() => { setSelectedProductForImages(product); setImageManagerOpen(true); }}
                  onManageVariants={() => { setSelectedProductForVariants(product); setVariantManagerOpen(true); }}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Modifier' : 'Nouveau produit'}</DialogTitle>
            <DialogDescription>Remplissez les informations</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="product-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <Select value={formData.categoryId} onValueChange={(v) => setFormData({ ...formData, categoryId: v })}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Prix (centimes)</Label>
              <Input type="number" value={formData.priceCents} onChange={(e) => setFormData({ ...formData, priceCents: e.target.value })} data-testid="product-price-input" />
              {formData.priceCents && <p className="text-xs text-muted-foreground">= {(parseInt(formData.priceCents) / 100).toFixed(2)} €</p>}
            </div>
            <div className="space-y-2 p-3 rounded-lg border border-dashed border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20">
              <Label className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                <Tag className="w-3.5 h-3.5" />
                Prix promotionnel
              </Label>
              <Input type="number" placeholder="Laisser vide = pas de promo" value={formData.promoPriceCents} onChange={(e) => setFormData({ ...formData, promoPriceCents: e.target.value })} data-testid="product-promo-price-input" />
              {formData.promoPriceCents && <p className="text-xs text-muted-foreground">= {(parseInt(formData.promoPriceCents) / 100).toFixed(2)} € (au lieu de {formData.priceCents ? (parseInt(formData.priceCents) / 100).toFixed(2) + ' €' : '–'})</p>}
              {formData.promoPriceCents && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Début promo</Label>
                    <Input type="date" value={formData.promoStartDate} onChange={(e) => setFormData({ ...formData, promoStartDate: e.target.value })} data-testid="promo-start-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fin promo</Label>
                    <Input type="date" value={formData.promoEndDate} onChange={(e) => setFormData({ ...formData, promoEndDate: e.target.value })} data-testid="promo-end-date" />
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Description</Label>
                {user?.plan === 'PREMIUM' && (
                  <div className="flex gap-1.5">
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1.5 rounded-full" onClick={handleCorrectDescription} disabled={correctingAI || generatingAI || !formData.description.trim()} data-testid="ai-correct-btn">
                      {correctingAI ? <Loader2 className="w-3 h-3 animate-spin" /> : <SpellCheck className="w-3 h-3" />}
                      {correctingAI ? 'Correction...' : 'Corriger'}
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1.5 rounded-full" onClick={handleGenerateDescription} disabled={generatingAI || correctingAI || !formData.name.trim()} data-testid="ai-generate-btn">
                      {generatingAI ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      {generatingAI ? 'Génération...' : 'Générer'}
                    </Button>
                  </div>
                )}
              </div>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder={user?.plan === 'PREMIUM' ? 'Entrez une description ou générez-la avec l\'IA' : 'Description du produit'} />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <ArrowUpRight className="w-3.5 h-3.5" />
                Produits upsell
              </Label>
              <p className="text-xs text-muted-foreground">Sélectionnez jusqu'à 4 produits à recommander. Si vide, les produits de la même catégorie seront affichés.</p>
              <div className="flex flex-wrap gap-1.5">
                {products
                  .filter(p => editingProduct ? p.id !== editingProduct.id : true)
                  .map(p => {
                    const isSelected = formData.upsellProductIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setFormData(prev => {
                            const ids = prev.upsellProductIds || [];
                            if (isSelected) return { ...prev, upsellProductIds: ids.filter(id => id !== p.id) };
                            if (ids.length >= 4) return prev;
                            return { ...prev, upsellProductIds: [...ids, p.id] };
                          });
                        }}
                        className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-accent'}`}
                        data-testid={`upsell-toggle-${p.id}`}
                      >
                        {p.name}
                      </button>
                    );
                  })}
              </div>
              {formData.upsellProductIds.length > 0 && (
                <p className="text-xs text-muted-foreground">{formData.upsellProductIds.length}/4 produits sélectionnés</p>
              )}
            </div>
            <div className="flex items-center justify-between">
              <Label>Disponible</Label>
              <Switch checked={formData.isAvailable} onCheckedChange={(c) => setFormData({ ...formData, isAvailable: c })} />
            </div>
            {(user?.plan === 'PRO' || user?.plan === 'PREMIUM') && (
              <div className="flex items-center justify-between">
                <Label>Mettre en avant</Label>
                <Switch checked={formData.isFeatured} onCheckedChange={(c) => setFormData({ ...formData, isFeatured: c })} />
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Manager Dialog */}
      <Dialog open={imageManagerOpen} onOpenChange={setImageManagerOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gérer les images</DialogTitle>
            <DialogDescription>{selectedProductForImages?.name}</DialogDescription>
          </DialogHeader>
          <ImageManager 
            product={selectedProductForImages}
            onUpload={(files) => selectedProductForImages && handleMultipleImageUpload(selectedProductForImages.id, files)}
            onDelete={(imageId) => selectedProductForImages && handleDeleteImage(selectedProductForImages.id, imageId)}
            onReorder={(newImages) => selectedProductForImages && handleReorderImages(selectedProductForImages.id, newImages)}
            uploading={uploadingImage}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le produit ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Variant Manager */}
      <VariantManager
        shopId={shopId}
        product={selectedProductForVariants}
        open={variantManagerOpen}
        onOpenChange={setVariantManagerOpen}
      />

      {/* CSV Import */}
      <CsvImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        api={api}
        shopId={shopId}
        onSuccess={fetchData}
      />
    </div>
  );
};

// Sortable product card wrapper
const SortableProductCard = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.product.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ProductCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
};

// Product card component
const ProductCard = ({ product, getCategoryName, formatPrice, onEdit, onDelete, onToggleFeatured, onManageImages, onManageVariants, dragHandleProps }) => {
  const imageCount = product.images?.length || 0;
  const firstImage = imageCount > 0 ? product.images[0] : null;
  const hasPromo = product.promoPriceCents && isPromoActive(product);

  return (
    <Card className="overflow-hidden" data-testid={`product-card-${product.id}`}>
      <div className="aspect-square bg-muted relative group">
        {firstImage ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${firstImage.url}`} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-muted-foreground" />
          </div>
        )}
        {imageCount > 0 && (
          <Badge className="absolute bottom-2 right-2 bg-black/70" variant="secondary">
            <Image className="w-3 h-3 mr-1" />{imageCount}
          </Badge>
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Button variant="secondary" size="sm" onClick={onManageImages} data-testid={`manage-images-${product.id}`}>
            <Image className="w-4 h-4 mr-2" />Gérer les images
          </Button>
        </div>
        {product.isFeatured && (
          <Badge className="absolute top-2 left-2 bg-yellow-500"><Star className="w-3 h-3 mr-1" />Mis en avant</Badge>
        )}
        {hasPromo && (
          <Badge className="absolute top-2 left-2 bg-red-500 text-white" style={product.isFeatured ? { top: '2.5rem' } : {}} data-testid={`promo-badge-${product.id}`}>
            <Tag className="w-3 h-3 mr-1" />PROMO
          </Badge>
        )}
        {!product.isAvailable && (
          <Badge variant="secondary" className="absolute top-2 right-2">Indisponible</Badge>
        )}
        {dragHandleProps && (
          <button
            className="absolute top-2 right-2 w-8 h-8 bg-black/60 hover:bg-black/80 text-white rounded-lg flex items-center justify-center cursor-grab active:cursor-grabbing z-10"
            title="Glisser pour réordonner"
            data-testid={`drag-handle-product-${product.id}`}
            {...dragHandleProps}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
      </div>
      <CardContent className="p-4">
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold line-clamp-1">{product.name}</h3>
              <p className="text-sm text-muted-foreground">{getCategoryName(product.categoryId)}</p>
            </div>
            {hasPromo ? (
              <div className="text-right">
                <span className="text-sm line-through text-muted-foreground">{formatPrice(product.priceCents)}</span>
                <span className="block font-bold text-red-500">{formatPrice(product.promoPriceCents)}</span>
              </div>
            ) : (
              <span className="font-bold text-primary">{formatPrice(product.priceCents)}</span>
            )}
          </div>
          {product.description && <p className="text-sm text-muted-foreground line-clamp-2">{product.description}</p>}
          <div className="flex items-center gap-2 pt-2">
            <Button variant="ghost" size="icon" onClick={onToggleFeatured} title="Mettre en avant">
              <Star className={`w-4 h-4 ${product.isFeatured ? 'fill-yellow-500 text-yellow-500' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={onManageVariants} title="Variantes">
              <Layers className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onEdit}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive" onClick={onDelete}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Sortable image item for ImageManager
const SortableImageItem = ({ image, index, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative aspect-square bg-muted rounded-lg overflow-hidden group">
      <img src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`} alt={`Image ${index + 1}`} className="w-full h-full object-cover" />
      <Badge className="absolute top-1 left-1 text-xs" variant="secondary">{index + 1}</Badge>
      <button
        className="absolute top-1 right-7 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        title="Glisser pour réordonner"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3 h-3" />
      </button>
      <button onClick={() => onDelete(image.id)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Supprimer" data-testid={`delete-image-${image.id}`}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Image manager with multiple upload and drag reorder
const ImageManager = ({ product, onUpload, onDelete, onReorder, uploading }) => {
  const images = product?.images || [];
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onUpload(files);
    }
    e.target.value = '';
  };

  const handleImageDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex(i => i.id === active.id);
    const newIndex = images.findIndex(i => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(images, oldIndex, newIndex);
    onReorder(reordered);
  };

  return (
    <div className="space-y-4">
      <div>
        <input type="file" id="image-upload-input" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
        <Button type="button" variant="outline" onClick={() => document.getElementById('image-upload-input').click()} disabled={uploading} className="w-full" data-testid="upload-image-btn">
          <Upload className="w-4 h-4 mr-2" />{uploading ? 'Envoi en cours...' : 'Ajouter des images'}
        </Button>
        <p className="text-xs text-muted-foreground mt-1.5 text-center">Vous pouvez sélectionner plusieurs images à la fois</p>
      </div>
      {images.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleImageDragEnd}>
          <SortableContext items={images.map(i => i.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-3 gap-3">
              {images.map((image, index) => (
                <SortableImageItem key={image.id} image={image} index={index} onDelete={onDelete} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-3 py-8 text-center text-muted-foreground">
            <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Aucune image</p>
          </div>
        </div>
      )}
      {images.length > 1 && (
        <p className="text-xs text-muted-foreground text-center">
          Glissez les images pour réordonner. La première image sera l'image principale.
        </p>
      )}
    </div>
  );
};

// Check if a promo is currently active based on dates
const isPromoActive = (product) => {
  if (!product.promoPriceCents) return false;
  const now = new Date();
  if (product.promoStartDate) {
    const start = new Date(product.promoStartDate);
    if (now < start) return false;
  }
  if (product.promoEndDate) {
    const end = new Date(product.promoEndDate + 'T23:59:59');
    if (now > end) return false;
  }
  return true;
};

export default ShopProducts;
